import { useEffect, useMemo, useState } from 'react';
import { navigate } from '../boot/router';
import { loadVersionDocument, listVersions, type VersionListItem } from '../data/versionRepo';
import type { CadDocument } from '../cad/model/document';
import { diffDocuments, summarize, type DocumentDiff } from '../cad/diff/documentDiff';
import { diffQuantities, quantityToTsv, type QuantityDiff } from '../cad/diff/quantityDiff';
import { buildCatalogMap } from '../cad/blocks/catalog';
import { FALLBACK_CATALOG } from '../cad/blocks/standardLibrary';
import { CompareCanvas } from '../cad/ui/CompareCanvas';
import { toMm } from '../cad/model/units';

/**
 * 버전 비교 화면 (작업지시서 P4-09 · 물량 P5-05)
 *
 * ── 이 화면이 이 제품의 존재 이유다 ────────────────────────────────────
 *
 *   지금은 사람이 도면 두 장을 나란히 놓고 눈으로 세다가 틀린다.
 *   여기서 "현행 → A안" 을 고르면 철거/신설/이설 수량과 추정 비용이 바로 나온다.
 *
 * ── 설계에서 양보하지 않은 것 ──────────────────────────────────────────
 *
 *   · **색만으로 구분하지 않는다.** 아이콘(+ − ↔ ✎)을 병기한다.
 *     건설 현장에서 흑백 출력이 흔하고, 색각 이상도 흔하다.
 *   · **이설을 철거+신설로 세지 않는다.** 옮긴 것은 옮긴 것이다.
 *     두 번 세면 견적이 두 배가 된다.
 *   · **짝짓기 기준을 화면에 밝힌다.** id 로 짝지었는지 모양으로 짝지었는지에
 *     따라 결과 해석이 달라지므로 숨기면 안 된다.
 */

const CATALOG = buildCatalogMap(FALLBACK_CATALOG);

type Tab = 'visual' | 'quantity' | 'list';

export function ComparePage({
  drawingId,
  a,
  b,
}: {
  drawingId: string;
  a: string;
  b: string;
}) {
  const [docA, setDocA] = useState<CadDocument | null>(null);
  const [docB, setDocB] = useState<CadDocument | null>(null);
  const [versions, setVersions] = useState<VersionListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>('visual');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);

    Promise.all([loadVersionDocument(a), loadVersionDocument(b), listVersions(drawingId)])
      .then(([da, db, vs]) => {
        if (!alive) return;
        setDocA(da);
        setDocB(db);
        setVersions(vs);
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [drawingId, a, b]);

  const diff = useMemo<DocumentDiff | null>(
    () => (docA && docB ? diffDocuments(docA, docB) : null),
    [docA, docB]
  );

  const quantity = useMemo<QuantityDiff | null>(
    () => (docA && docB ? diffQuantities(docA, docB, CATALOG) : null),
    [docA, docB]
  );

  const label = (id: string) => {
    const v = versions.find((x) => x.id === id);
    return v ? v.name || `v${v.version_number}` : id.slice(0, 8);
  };

  if (loading) return <div className="page"><p className="muted">불러오는 중…</p></div>;
  if (error) {
    return (
      <div className="page">
        <div className="empty">
          <h2>비교할 수 없습니다</h2>
          <p className="muted">{error}</p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => navigate({ name: 'drawing', drawingId })}
          >
            도면으로 돌아가기
          </button>
        </div>
      </div>
    );
  }
  if (!diff || !quantity || !docA || !docB) return null;

  const s = summarize(diff);

  return (
    <div className="page cmp">
      <header className="cmp__head">
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => navigate({ name: 'drawing', drawingId })}
        >
          ← 도면
        </button>
        <h1 className="cmp__title">
          <span className="cmp__side cmp__side--before">{label(a)}</span>
          <span aria-hidden="true"> → </span>
          <span className="cmp__side cmp__side--after">{label(b)}</span>
        </h1>

        {/* 짝짓기 기준을 숨기지 않는다 — 결과 해석이 달라진다 */}
        <span className="cmp__strategy" title={STRATEGY_HELP[diff.strategy]}>
          {diff.strategy === 'id' ? '같은 계보 비교' : '모양으로 대조'}
        </span>
      </header>

      <div className="cmp__summary">
        <Stat icon="＋" label="신설" value={s.addedCount} tone="add" />
        <Stat icon="−" label="철거" value={s.removedCount} tone="del" />
        <Stat icon="↔" label="이설" value={s.movedCount} tone="move" />
        <Stat icon="✎" label="변경" value={s.modifiedCount} tone="mod" />
        <Stat icon="＝" label="유지" value={s.unchangedCount} tone="keep" />
      </div>

      {!s.hasChanges && (
        <p className="cmp__none">두 버전이 같습니다. 달라진 것이 없습니다.</p>
      )}

      <nav className="cmp__tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'visual'} className="cmp__tab" onClick={() => setTab('visual')}>
          도면
        </button>
        <button type="button" role="tab" aria-selected={tab === 'quantity'} className="cmp__tab" onClick={() => setTab('quantity')}>
          물량·견적
        </button>
        <button type="button" role="tab" aria-selected={tab === 'list'} className="cmp__tab" onClick={() => setTab('list')}>
          변경 목록
        </button>
      </nav>

      {tab === 'visual' && (
        <CompareCanvas before={docA} after={docB} diff={diff} beforeLabel={label(a)} afterLabel={label(b)} />
      )}
      {tab === 'quantity' && <QuantityTable q={quantity} />}
      {tab === 'list' && <ChangeList diff={diff} unit={docB.displayUnit} />}
    </div>
  );
}

const STRATEGY_HELP: Record<DocumentDiff['strategy'], string> = {
  id: '같은 도면에서 갈라져 나온 버전입니다. 객체 고유번호로 짝지었으므로, 지우고 새로 넣은 것은 이설이 아니라 철거+신설로 셉니다.',
  shape: '고유번호가 겹치지 않아(도면 재업로드 등) 모양과 위치로 짝지었습니다. 같은 규격이 여러 개면 짝이 어긋날 수 있습니다.',
};

function Stat({
  icon,
  label,
  value,
  tone,
}: {
  icon: string;
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <div className={`cmp-stat cmp-stat--${tone}`}>
      {/* 색 외에 아이콘을 병기한다 — 흑백 출력·색각 이상 대응 */}
      <span className="cmp-stat__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="cmp-stat__value">{value}</span>
      <span className="cmp-stat__label">{label}</span>
    </div>
  );
}

/* -------------------------------------------------------------------- 물량 */

function QuantityTable({ q }: { q: QuantityDiff }) {
  const [copied, setCopied] = useState(false);
  const t = q.totals;

  return (
    <section className="cmp__pane">
      <div className="cmp__paneHead">
        <h2>리뉴얼 물량</h2>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(quantityToTsv(q));
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            } catch {
              setCopied(false);
            }
          }}
        >
          {copied ? '복사됨 ✓' : '엑셀로 복사'}
        </button>
      </div>

      {q.unknownBlocks.length > 0 && (
        // 조용히 빼면 견적에서 누락된다. 반드시 보여준다.
        <p className="cmp__warn" role="alert">
          카탈로그에 없어 집계하지 못한 블록: {q.unknownBlocks.join(', ')} — 이 항목은 물량과
          비용에 포함되지 않았습니다.
        </p>
      )}

      <div className="table-wrap">
        <table className="table table--compact">
          <thead>
            <tr>
              <th>품목</th>
              <th className="num">현행</th>
              <th className="num">변경후</th>
              <th className="num">철거</th>
              <th className="num">신설</th>
              <th className="num">이설</th>
              <th className="num">유지</th>
              <th className="num">진열 증감(m)</th>
              <th className="num">추정 비용</th>
            </tr>
          </thead>
          <tbody>
            {q.rows.map((r) => (
              <tr key={r.code}>
                <td>{r.name}</td>
                <td className="num">{r.beforeCount}</td>
                <td className="num">{r.afterCount}</td>
                <td className="num">{r.demolish || ''}</td>
                <td className="num">{r.install || ''}</td>
                <td className="num">{r.relocate || ''}</td>
                <td className="num muted">{r.keep || ''}</td>
                <td className="num">{fmtDelta(r.shelfDeltaM)}</td>
                <td className="num">{r.costKrw.toLocaleString('ko-KR')}</td>
              </tr>
            ))}
            {q.rows.length === 0 && (
              <tr>
                <td colSpan={9} className="muted">
                  집계할 집기가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <th>합계</th>
              <th className="num" />
              <th className="num" />
              <th className="num">{t.demolish || ''}</th>
              <th className="num">{t.install || ''}</th>
              <th className="num">{t.relocate || ''}</th>
              <th className="num muted">{t.keep || ''}</th>
              <th className="num">{fmtDelta(t.shelfDeltaM)}</th>
              <th className="num">{t.costKrw.toLocaleString('ko-KR')}원</th>
            </tr>
          </tfoot>
        </table>
      </div>

      <dl className="cmp__facts">
        <dt>진열 연장</dt>
        <dd>
          {t.shelfBeforeM.toFixed(1)} m → {t.shelfAfterM.toFixed(1)} m ({fmtDelta(t.shelfDeltaM)} m,{' '}
          {fmtDelta(t.shelfDeltaPercent)}%)
        </dd>
        <dt>점유 면적</dt>
        <dd>
          {t.footprintBeforeM2.toFixed(1)} ㎡ → {t.footprintAfterM2.toFixed(1)} ㎡
        </dd>
        {t.wallDeltaM !== 0 && (
          <>
            <dt>벽체 연장</dt>
            <dd>{fmtDelta(t.wallDeltaM)} m — 구조 변경이 포함되어 있습니다</dd>
          </>
        )}
      </dl>

      <p className="cmp__note">
        추정 비용은 카탈로그 단가 기준입니다. 이설은 자재비 없이 설치비만 계산합니다. 실제
        견적은 구매부서 단가로 다시 산정하세요.
      </p>
    </section>
  );
}

function fmtDelta(v: number): string {
  if (v === 0) return '—';
  return v > 0 ? `+${v}` : String(v);
}

/* -------------------------------------------------------------- 변경 목록 */

function ChangeList({ diff, unit }: { diff: DocumentDiff; unit: 'mm' | 'cm' | 'm' }) {
  return (
    <section className="cmp__pane">
      <Group icon="＋" tone="add" title="신설" items={diff.added.map((e) => `${e.type} · ${e.layer}`)} />
      <Group icon="−" tone="del" title="철거" items={diff.removed.map((e) => `${e.type} · ${e.layer}`)} />
      <Group
        icon="↔"
        tone="move"
        title="이설"
        items={diff.moved.map(
          (m) =>
            `${m.after.type} · ${m.after.layer} — ${fmtLen(m.dx, unit)}, ${fmtLen(m.dy, unit)} 이동`
        )}
      />
      <Group
        icon="✎"
        tone="mod"
        title="변경"
        items={diff.modified.map((m) => `${m.after.type} · ${m.after.layer} — ${m.changes.join(', ')}`)}
      />

      {(diff.layers.added.length > 0 ||
        diff.layers.removed.length > 0 ||
        diff.layers.changed.length > 0) && (
        <Group
          icon="▤"
          tone="mod"
          title="레이어"
          items={[
            ...diff.layers.added.map((l) => `추가: ${l.code}`),
            ...diff.layers.removed.map((l) => `삭제: ${l.code}`),
            ...diff.layers.changed.map((c) => `변경: ${c.after.code}`),
          ]}
        />
      )}
    </section>
  );
}

function fmtLen(um: number, unit: 'mm' | 'cm' | 'm'): string {
  const v = unit === 'mm' ? toMm(um) : unit === 'cm' ? toMm(um) / 10 : toMm(um) / 1000;
  return `${v.toFixed(unit === 'mm' ? 0 : 1)}${unit}`;
}

function Group({
  icon,
  tone,
  title,
  items,
}: {
  icon: string;
  tone: string;
  title: string;
  items: string[];
}) {
  if (items.length === 0) return null;
  const shown = items.slice(0, 200);
  return (
    <div className={`cmp-group cmp-group--${tone}`}>
      <h3 className="cmp-group__title">
        <span aria-hidden="true">{icon}</span> {title} <span className="muted">{items.length}</span>
      </h3>
      <ul className="cmp-group__list">
        {shown.map((t, i) => (
          <li key={i}>{t}</li>
        ))}
      </ul>
      {items.length > shown.length && (
        <p className="muted">…외 {items.length - shown.length}건</p>
      )}
    </div>
  );
}
